import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Ticket } from '@models/Ticket';
import { TicketDetail } from '@models/TicketDetail';
import { Archive } from '@models/Archive';
import { LogFile } from '@models/LogFile';
import { LogMessage } from '@models/LogMessage';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { TicketService } from '@services/ticket.service';
import { SnackbarService } from '@services/support/snackbar.service';
import { LoaderService } from '@services/support/loader.service';
import { MultilanguageService } from '@services/support/multilanguage.service';
import { FileShareService } from '@services/support/file-share.service';
import { UtilityService } from '@services/remote/utility.service';
import { RoomService } from '@services/remote/room.service';
import { SessionService } from '@services/remote/session.service';
import { CallService } from '@services/remote/call.service';
import { PdfExportService } from '@services/pdf-export.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { DetailPageService } from '@services/detail-page.service';
import { SessionDetailsComponent } from '../../sessions/session-details/session-details.component';
import { AlertModalComponent } from 'src/app/components/modals/alert-modal/alert-modal.component';
import { ImageModalComponent } from 'src/app/components/modals/image-modal/image-modal.component';
import { environment } from 'src/environments/environment';
import { ModalService } from '@services/remote/modal.service';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

@Component({
    selector: 'app-ticket-detail',
    templateUrl: './ticket-detail.component.html',
    styleUrls: ['./ticket-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TicketDetailComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild("pickSessionTemplate", { static: true }) private pickSessionTemplate: TemplateRef<any>;
  @ViewChild("ticketExportTemplate", { static: true }) private ticketExportTemplate: TemplateRef<any>;

  @Input() selectedTicket: Partial<Ticket>;
  @Input() mode: string;
  @Output() selectedTicketChange: EventEmitter<any> = new EventEmitter();
  @Output() deletedTicket: EventEmitter<any> = new EventEmitter();
  @Output() ticketUpdated: EventEmitter<any> = new EventEmitter();
  @Output() ticketCreated: EventEmitter<any> = new EventEmitter();

  onClose: Subject<void> = new Subject();
  closeModal: EventEmitter<any> = new EventEmitter();  // For image modal

  
  isLoadingDetail = false;
  isLoadingUpdate = false;
  isLoadingCreate = false;

  exportModalRef: BsModalRef | null = null;
  isExporting = false;
  exportError = '';
  exportOptions = {
    includeSessions: true,
    includeFiles: true,
    includeChat: true,
    includeImagesInPdf: false
  };

  ticketId: string;
  viewMode = true;

  ticketStatuses: any[] = [{ id: "open", name: "Open" }, { id: "in-progress", name: "In Progress" }, { id: "closed", name: "Closed" }];
  ticketPriorities: any[] = [{ id: "low", name: "Low" }, { id: "medium", name: "Medium" }, { id: "high", name: "High" }];
  ticketTypes: any[] = [];

  rooms: any[] = [];
  users: any[] = [];
  timezone = this.getBrowserTimeZone();

  selectedTicketRoomId: string = null;
  selectedTicketRoomUsers: any[] = [];

  ticketDetail: TicketDetail;
  isExpert: boolean = null;
  isAdmin: boolean = null;

  tabIndex = 0;
  ticketSessions: any[] = [];
  sessionStats: any = null;
  originalTicket: any = null; // Orijinal ticket bilgilerini tutar
  selectedSessionId: string | null = null;
  attachedSessionIds: Set<string> = new Set();
  quickSession: any = null;
  quickFiles: LogFile[] = [];
  quickArchives: Archive[] = [];
  quickLogs: LogMessage[] = [];
  quickFilesLoading = false;
  quickArchivesLoading = false;
  quickLogsLoading = false;
  quickFilesSub: Subscription | null = null;
  quickArchivesSub: Subscription | null = null;
  quickLogsSub: Subscription | null = null;
  quickArchiveLinks: { [key: string]: { loading: boolean, url?: string } } = {};

  // Files-related properties
  isLoadingFiles = true;
  files: any[] = [];
  pendingFiles: File[] = [];  // Files waiting to be uploaded during create
  pendingFileUrls: Map<File, string> = new Map();  // Cache for blob URLs
  imageExtensions: string[] = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'];
  pdfExtensions: string[] = ['pdf'];
  objectSpecificExtensions: string[] = ['obj', 'stl', 'gltf', 'glb', 'dae', 'scn'];

  ticketForm: FormGroup = new FormGroup({
    title: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    room_id: new FormControl('', Validators.required),
    type: new FormControl('service-ticket'),
    status: new FormControl('open'),
    priority: new FormControl('low'),
    description: new FormControl('', Validators.maxLength(5000)),
    resolution: new FormControl('', Validators.maxLength(10000)),
    followup: new FormControl(null),
    assignee: new FormControl(null),
  });

  followupDateConfig = {
    dateInputFormat: 'DD-MM-YYYY',
    containerClass: 'theme-blue',
    customTodayClass: 'datepicker-today'
  };

  get status() {
    return this.ticketForm.get('status');
  }

  get priority() {
    return this.ticketForm.get('priority');
  }

  get title() {
    return this.ticketForm.get('title');
  }

  get description() {
    return this.ticketForm.get('description');
  }

  get resolution() {
    return this.ticketForm.get('resolution');
  }

  get roomId() {
    return this.ticketForm.get('room_id');
  }

  private subscriptions: Subscription[] = [];
  private ticketSessionsSub: Subscription | null = null;
  private ticketFilesSub: Subscription | null = null;
  private lastValidValues: { [key: string]: string } = {};
  focusedField: 'title' | 'description' | 'resolution' | null = null;

  constructor(
    private ticketService: TicketService,
    private translateService: TranslateService,
    private snackbarService: SnackbarService,
    private authService: AuthService,
    private loaderService: LoaderService,
    @Optional() public modalRef: BsModalRef,
    private modal: BsModalService,
    private multilanguageService: MultilanguageService,
    private detailPageService: DetailPageService,
    private modalService: BsModalService,
    private roomService: RoomService,
    private sessionService: SessionService,
    private templateModalService: ModalService,
    private fileShareService: FileShareService,
    private pdfExportService: PdfExportService,
    private callService: CallService
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.ticketSessionsSub?.unsubscribe();
    this.ticketFilesSub?.unsubscribe();
    this.clearQuickSessionSubs();
    this.onClose.complete();
  }

  ngOnInit(): void {
    this.isExpert = this.authService.currentUser?.role === 'expert';
    this.isAdmin = this.roomService.hasFullRoomAccess();

    // Admin ve co-admin kullanıcıları için tüm odaları al, normal kullanıcılar için sadece kendi odalarını
    if (this.isAdmin) {
      this.subscriptions.push(this.roomService.allRooms.subscribe(rooms => {
        if (rooms) {
          this.rooms = rooms
            .filter(r => r.room_data && !r.room_data.deleted && !r.room_data.personal_room && !r.room_data.meeting_room)
            .map(r => ({ id: r.id, name: r.room_data.name }))
            .sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));

          if (this.mode === 'create' && !this.ticketForm.get('room_id')?.value) {
            this.ticketForm.patchValue({ room_id: this.rooms[0]?.id || null });
          }
        }
      }));
    } else {
      this.subscriptions.push(this.authService.user.subscribe(user => {
        if (user) {
          this.timezone = this.getBrowserTimeZone();
          this.rooms = ((user && user.rooms) ? Object.keys(user.rooms).map(rid => { return { id: rid, name: user.rooms[rid].name }; }) : [])
            .sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));

          if (this.mode === 'create' && !this.ticketForm.get('room_id')?.value) {
            this.ticketForm.patchValue({ room_id: this.rooms[0]?.id || null });
          }
        }
      }));
    }

    // Load users - for field users use getUserContacts, for admin/expert use getAllUsers
    if (this.authService.currentUser?.role === 'user') {
      this.callService.getUserContacts().then(users => {
        if (users) {
          // Add current user to the list if not already present
          const currentUser = this.authService.currentUser;
          const allUsers = [...users];
          if (currentUser && !allUsers.find(u => u.id === currentUser.id)) {
            allUsers.push({ id: currentUser.id, name: currentUser.name, rooms: currentUser.rooms });
          }
          this.users = allUsers.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
          this.selectedTicketRoomUsers = this.selectedTicketRoomId ? this.users.filter(u => u.rooms && u.rooms[this.selectedTicketRoomId]) : [];
        } else {
          this.users = [];
        }
      }).catch(error => {
        this.users = [];
        this.selectedTicketRoomUsers = [];
      });
    } else {
      this.subscriptions.push(this.authService.getAllUsers(true).subscribe(
        users => {
          if (users) {
            this.users = [...users].sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
            this.selectedTicketRoomUsers = this.selectedTicketRoomId ? this.users.filter(u => u.rooms && u.rooms[this.selectedTicketRoomId]) : [];
          } else {
            this.users = [];
          }
        },
        error => {
          this.users = [];
          this.selectedTicketRoomUsers = [];
        }
      ));
    }

    // Load ticket types from service
    this.ticketService.getTicketTypes()
      .then(res => {
        this.ticketTypes = res?.types || [{ id: 'service-ticket', name: 'Service Ticket' }];
      })
      .catch(error => {
        console.error('Error loading ticket types:', error);
        // Fallback to default type
        this.ticketTypes = [{ id: 'service-ticket', name: 'Service Ticket' }];
      });

    // Watch for room changes to update assignee options
    this.subscriptions.push(this.ticketForm.get('room_id').valueChanges.subscribe(roomId => {
      this.selectedTicketRoomId = roomId;
      this.selectedTicketRoomUsers = roomId ? this.users.filter(u => u.rooms && u.rooms[roomId]) : [];
      // Clear assignee if current assignee is not in the new room
      const currentAssignee = this.ticketForm.get('assignee').value;
      if (currentAssignee && this.selectedTicketRoomUsers.findIndex(u => u.id === currentAssignee) < 0) {
        this.ticketForm.patchValue({ assignee: null });
      }
    }));

    if (this.mode === 'create') {
      this.viewMode = false;
      this.isLoadingDetail = false;
      this.ticketForm.patchValue({
        room_id: this.rooms[0]?.id,
        type: 'service-ticket',
        status: 'open',
        priority: 'low'
      });
      // Create mode için orijinal ticket bilgilerini boş tut
      this.originalTicket = null;
    } else if (this.mode === 'view' && this.selectedTicket) {
      this.viewMode = true;
      this.getTicketDetail();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTicket'] && this.selectedTicket) {
      this.getTicketDetail();
      this.viewMode = this.mode === 'view';
    }
    if (changes['mode'] && this.mode === 'create') {
      this.viewMode = false;
      this.isLoadingDetail = false;
      this.originalTicket = null; // Create mode için sıfırla
      this.clearPendingFiles();  // Clear pending files when entering create mode
      this.tabIndex = 1;  // Reset to Details tab (Chat+Timeline is not available in create mode)
      this.ticketForm.reset({
        room_id: this.rooms[0]?.id,
        type: 'service-ticket',
        status: 'open',
        priority: 'low'
      });
    }
  }

  loadTicketData(): void {
    if (!this.selectedTicket) return;

    const roomId = this.selectedTicket.room_id || this.selectedTicket.room?.id;
    
    // Normal kullanıcılar için: room rooms listesinde yoksa ekle
    // Admin için: RoomService.allRooms zaten tüm odaları getiriyor, ekleme yapma
    if (!this.isAdmin && roomId && !this.rooms.find(r => r.id === roomId)) {
      const roomName = this.selectedTicket.room?.name || this.ticketDetail?.room?.name || 'Unknown';
      this.rooms = [...this.rooms, { id: roomId, name: roomName }]
        .sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
    }

    const assigneeId = this.ticketDetail?.assignee?.id
      || (typeof this.ticketDetail?.assignee === 'string' ? this.ticketDetail.assignee : null)
      || null;

    const ticketData = {
      title: this.selectedTicket.title || '',
      room_id: roomId,
      type: this.selectedTicket.type || 'service-ticket',
      status: this.selectedTicket.status || 'open',
      priority: this.selectedTicket.priority || 'low',
      description: this.selectedTicket.description || '',
      resolution: this.selectedTicket.resolution || '',
      followup: this.selectedTicket.followup || null,
      assignee: assigneeId,
    };

    // Orijinal ticket bilgilerini sakla
    this.originalTicket = { ...ticketData };
    
    this.ticketForm.patchValue(ticketData);
  }

  getTicketDetail(): void {
    if (!this.selectedTicket?.id) return;

    this.ticketSessionsSub?.unsubscribe();
    this.ticketFilesSub?.unsubscribe();
    this.ticketSessionsSub = null;
    this.ticketFilesSub = null;
    this.ticketSessions = [];
    this.sessionStats = null;
    this.files = [];
    this.isLoadingFiles = true;

    this.isLoadingDetail = true;
    this.loaderService.show();
    this.ticketService.getTicketDetail(this.selectedTicket.id)
      .then(res => {
        if (res && res.ticket) {
          this.ticketDetail = res.ticket;
          
          // Update selectedTicket with full details including priority and room
          this.selectedTicket = {
            ...this.selectedTicket,
            title: this.ticketDetail.title,
            description: this.ticketDetail.description,
            resolution: this.ticketDetail.resolution || '',
            status: this.ticketDetail.status,
            priority: this.ticketDetail.priority || 'low',
            type: this.ticketDetail.type,
            room_id: this.ticketDetail.room?.id,
            room: this.ticketDetail.room,
            create_time: this.ticketDetail.create_time,
            followup: this.ticketDetail.followup ? new Date(this.ticketDetail.followup) : null,
          };
          
          // Set selectedTicketRoomId and update room users for assignee dropdown
          this.selectedTicketRoomId = this.ticketDetail.room?.id;
          this.selectedTicketRoomUsers = this.selectedTicketRoomId ? this.users.filter(u => u.rooms && u.rooms[this.selectedTicketRoomId]) : [];
          
          this.loadTicketData();
          
          // Load assignee after room users are set
          if (this.ticketDetail.assignee) {
            this.ticketForm.patchValue({ assignee: this.ticketDetail.assignee.id });
          }

          // Load attached sessions
          this.ticketSessionsSub = this.ticketService.getTicketSessions(this.selectedTicket.id)
            .subscribe(
              sessions => {
                this.ticketSessions = sessions.sort((a, b) => a.create_time > b.create_time ? -1 : 1);
                this.attachedSessionIds = new Set((sessions || []).map(session => session.id));
                this.calculateSessionStats(sessions)
                  .then(stats => {
                    this.sessionStats = stats;
                  });
              },
              error => {
                // Logout sırasında permission denied hatalarını sustur
                const errorMsg = error?.message || error?.toString() || '';
                if (!errorMsg.toLowerCase().includes('permission')) {
                  console.error('Error loading ticket sessions:', error);
                }
              }
            );

          // Load ticket files
          this.ticketFilesSub = this.ticketService.getTicketFiles(this.selectedTicket.id)
            .subscribe(
              files => {
                if (files) {
                  this.files = Object.values(files);
                } else {
                  this.files = [];
                }
                this.isLoadingFiles = false;
              },
              error => {
                // Logout sırasında permission denied hatalarını sustur
                const errorMsg = error?.message || error?.toString() || '';
                if (!errorMsg.toLowerCase().includes('permission')) {
                  console.error('Error loading ticket files:', error);
                }
                this.isLoadingFiles = false;
              }
            );
        }
        this.isLoadingDetail = false;
        this.loaderService.hide();
      })
      .catch(error => {
        console.error('Error loading ticket detail:', error);
        this.isLoadingDetail = false;
        this.loaderService.hide();
        this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.TICKETS.LOAD_ERROR'));
      });
  }

  back(): void {
    // Subscriptions'ı temizle
    this.ticketSessionsSub?.unsubscribe();
    this.ticketFilesSub?.unsubscribe();
    this.ticketSessionsSub = null;
    this.ticketFilesSub = null;

    if (this.modalRef && this.modalRef.content === this) {
      this.modalRef.hide();
      return;
    }
    
    this.selectedTicketChange.emit(null);
  }

  editTicket(): void {
    this.tabIndex = 0;
    this.viewMode = false;
  }

  changeStatus(status: string): void {
    this.ticketForm.patchValue({ status });
  }

  changePriority(priority: string): void {
    this.ticketForm.patchValue({ priority });
  }

  enforceMaxLength(controlName: 'title' | 'description' | 'resolution', maxLength: number): void {
    if (this.mode !== 'create') {
      return;
    }
    const control = this.ticketForm.get(controlName);
    if (!control) {
      return;
    }

    const currentValue = String(control.value || '');
    if (currentValue.length <= maxLength) {
      this.lastValidValues[controlName] = currentValue;
      return;
    }

    const previousValue = this.lastValidValues[controlName] ?? '';
    control.setValue(previousValue, { emitEvent: false });
  }

  guardPaste(event: ClipboardEvent, controlName: 'title' | 'description' | 'resolution', maxLength: number): void {
    if (this.mode !== 'create') {
      return;
    }
    const control = this.ticketForm.get(controlName);
    const pastedText = event.clipboardData?.getData('text') ?? '';
    if (!control || !pastedText) {
      return;
    }

    const input = event.target as HTMLInputElement | HTMLTextAreaElement | null;
    const currentValue = String(control.value || '');
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;
    const nextLength = currentValue.length - (selectionEnd - selectionStart) + pastedText.length;

    if (nextLength > maxLength) {
      event.preventDefault();
    }
  }

  getCharCount(controlName: 'title' | 'description' | 'resolution'): number {
    const control = this.ticketForm.get(controlName);
    return String(control?.value || '').length;
  }

  formatCount(value: number): string {
    return new Intl.NumberFormat().format(value || 0);
  }

  getCounterText(controlName: 'title' | 'description' | 'resolution', maxLength: number): string {
    const count = this.getCharCount(controlName);
    return `${this.formatCount(count)} / ${this.formatCount(maxLength)}`;
  }

  getCounterClass(controlName: 'title' | 'description' | 'resolution', maxLength: number): string {
    const count = this.getCharCount(controlName);
    const ratio = maxLength > 0 ? count / maxLength : 0;

    if (ratio >= 1) {
      return 'counter-danger';
    }
    if (ratio >= 0.95) {
      return 'counter-amber';
    }
    if (ratio >= 0.85) {
      return 'counter-warn';
    }
    return 'counter-muted';
  }

  setFocusedField(controlName: 'title' | 'description' | 'resolution' | null): void {
    this.focusedField = controlName;
  }

  shouldShowCounter(controlName: 'title' | 'description' | 'resolution', maxLength: number): boolean {
    return this.focusedField === controlName || this.getCharCount(controlName) > maxLength;
  }

  saveChanges(): void {
    if (!this.ticketForm.valid) {
      this.ticketForm.markAllAsTouched();
      this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.TICKETS.FORM_INVALID'));
      return;
    }

    const formValue = this.ticketForm.value;
    
    // Sadece değişen alanları tespit et
    const changedProperties = this.detectTicketChanges(formValue, this.originalTicket);
    
    // Eğer hiçbir değişiklik yoksa kaydetme
    if (Object.keys(changedProperties).length === 0) {
      this.viewMode = true;
      return;
    }

    // Check if work group is being changed and there are attached sessions
    if (changedProperties.hasOwnProperty('room_id')) {
      const currentRoomId = this.originalTicket?.room_id;
      const attachedSessionsInCurrentRoom = this.ticketSessions.filter(
        session => session.room_id === currentRoomId
      );

      if (attachedSessionsInCurrentRoom.length > 0) {
        // Show warning modal
        const count = attachedSessionsInCurrentRoom.length;
        const messageKey = count === 1 
          ? 'MAIN.CONTENT.TICKETS.WORK_GROUP_CHANGE_CONFIRM_SINGULAR'
          : 'MAIN.CONTENT.TICKETS.WORK_GROUP_CHANGE_CONFIRM';
        
        const initialState = {
          headerTitle: this.translateService.instant('MAIN.CONTENT.TICKETS.WORK_GROUP_CHANGE_WARNING'),
          textMessage: this.translateService.instant(messageKey, { count }),
          confirmButtonText: this.translateService.instant('MAIN.CONTENT.TICKETS.SAVE_CHANGES'),
          cancelButtonText: this.translateService.instant('MAIN.CONTENT.TICKETS.CANCEL')
        };
        
        const alertModalRef = this.modal.show(AlertModalComponent, {
          initialState,
          backdrop: 'static',
          class: 'modal-dialog-centered',
          animated: false
        });

        alertModalRef.content.onClose.subscribe(confirmed => {
          if (confirmed) {
            this.performSave(changedProperties);
          } else {
            alertModalRef.hide();
          }
        });
        return;
      }
    }

    // No work group change or no attached sessions, proceed with save
    this.performSave(changedProperties);
  }

  private performSave(changedProperties: any): void {
    this.isLoadingUpdate = true;
    this.loaderService.show();
    
    const changes = {
      id: this.selectedTicket.id,
      properties: changedProperties
    };

    const changeEvents = this.buildTicketChangeEvents(changedProperties, this.originalTicket);

    this.ticketService.updateTicket(changes)
      .then(() => {
        this.isLoadingUpdate = false;
        this.loaderService.hide();
        this.snackbarService.success(this.translateService.instant('MAIN.CONTENT.TICKETS.UPDATE_SUCCESS'));

        if (changeEvents.length > 0) {
          changeEvents.forEach(event => {
            this.ticketService.addTicketChatMessage(this.selectedTicket.id, {
              text: this.buildSystemChatText(event.key, event.params),
              userId: this.authService.currentUser?.id,
              timestamp: Date.now()
            }).catch(() => {});
          });
        }
        
        // Reload ticket details from server to get the updated values
        this.getTicketDetail();
        
        // Emit updated ticket with id so parent can update the list
        this.ticketUpdated.emit({ ...changedProperties, id: this.selectedTicket.id });
        this.viewMode = true;
      })
      .catch((error) => {
        this.isLoadingUpdate = false;
        this.loaderService.hide();
        this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.TICKETS.UPDATE_ERROR'));
      });
  }

  createTicket(): void {
    if (!this.ticketForm.valid) {
      this.ticketForm.markAllAsTouched();
      this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.TICKETS.FORM_INVALID'));
      return;
    }

    this.isLoadingCreate = true;
    this.loaderService.show();

    const formValue = this.ticketForm.value;
    const ticketData: any = {
      type: formValue.type,
      creator: this.authService.currentUser.id,
      description: formValue.description,
      priority: formValue.priority,
      resolution: formValue.resolution,
      room_id: formValue.room_id,
      status: formValue.status,
      title: formValue.title,
      assignee: formValue.assignee
    };

    if (formValue.followup) {
      ticketData.followup = this.formatDate(formValue.followup);
    }

    this.ticketService.createTicket(ticketData)
      .then((newTicket) => {
        // Upload pending files if any
        if (this.pendingFiles.length > 0 && newTicket?.ticket_id) {
          const formData = new FormData();
          this.pendingFiles.forEach(file => {
            formData.append(file.name, file);
          });
          return this.ticketService.uploadTicketFiles(formData, newTicket.ticket_id, this.authService.currentUser.token)
            .then(() => newTicket);
        }
        return newTicket;
      })
      .then((newTicket) => {
        this.isLoadingCreate = false;
        this.loaderService.hide();
        this.clearPendingFiles();  // Clear pending files
        this.snackbarService.success(this.translateService.instant('MAIN.CONTENT.TICKETS.CREATE_SUCCESS'));
        this.ticketCreated.emit(newTicket);
      })
      .catch((error) => {
        this.isLoadingCreate = false;
        this.loaderService.hide();
        this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.TICKETS.CREATE_ERROR'));
      });
  }

  deleteTicket(): void {
    const initialState = {
      headerTitle: this.translateService.instant('MAIN.CONTENT.TICKETS.DELETE'),
      textMessage: this.translateService.instant('MAIN.CONTENT.TICKETS.DELETE_CONFIRM'),
      confirmButtonText: this.translateService.instant('MAIN.CONTENT.TICKETS.DELETE')
    };
    let alertmodalRef = this.modal.show(AlertModalComponent, {
      initialState,
      backdrop: 'static',
      class: 'modal-dialog-centered',
      animated: false
    });
    alertmodalRef.content.onClose.subscribe(res => {
      if (res) {
        this.deletedTicket.emit(this.selectedTicket.id);
      } else {
        alertmodalRef.hide();
      }
    });
  }

  getRoomName(roomId: string): string {
    // Önce rooms listesinde ara
    const room = this.rooms.find(r => r.id === roomId);
    if (room) {
      return room.name;
    }
    
    // Rooms listesinde yoksa, ticketDetail'den al (admin için gerekli)
    if (this.ticketDetail && this.ticketDetail.room && this.ticketDetail.room.id === roomId) {
      return this.ticketDetail.room.name;
    }
    
    // selectedTicket'ten de dene
    if (this.selectedTicket && this.selectedTicket.room && this.selectedTicket.room.id === roomId) {
      return this.selectedTicket.room.name;
    }
    
    return 'Unknown';
  }

  getTicketTypeName(typeId: string): string {
    if (!typeId) {
      return '-';
    }
    const type = this.ticketTypes.find(t => t.id === typeId);
    return type ? type.name : typeId;
  }

  formatDate(d: Date): string {
    return `${d.getFullYear()}-${("0"+(d.getMonth()+1)).slice(-2)}-${("0"+d.getDate()).slice(-2)}`;
  }

  private getBrowserTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local';
  }

  onTabClicked(index: number): void {
    this.tabIndex = index;
  }

  refreshFiles(): void {
    // Files subscription in getTicketDetail() will automatically update
  }

  getUsersName(id: string): string {
    const user = this.users.find(u => u.id === id);
    return user ? user.name : 'Unknown';
  }

  isUserListArray(users: any): boolean {
    return Array.isArray(users);
  }

  async calculateSessionStats(sessions: any[]) {
    return new Promise(resolve => {
      const stats: any = {};
      stats.count = sessions.length;
      stats.duration = 0;
      stats.users = {};
      sessions.forEach(session => {
        // Her session'u ayrı ayrı ceil ile yuvarlayıp topluyoruz (listedeki gösterimle tutarlı olması için)
        const sessionDuration = session.start_time && session.end_time ? 
          Math.ceil((session.end_time - session.start_time) / 60000) : 0;
        stats.duration = stats.duration + sessionDuration;
        (session.users ? Object.keys(session.users) : []).forEach(userId => {
          if (!stats.users[userId]) {
            stats.users[userId] = {
              count: 0,
              duration: 0
            }
          }
          stats.users[userId].count = stats.users[userId].count + 1;
          stats.users[userId].duration = session.user_durations && session.user_durations[userId] ? session.user_durations[userId] : 0;
        });
      });
      stats.duration = `${stats.duration} min`
      resolve(stats);
    });
  }

  onTimelineSessionSelected(sessionId: string): void {
    if (!sessionId) {
      return;
    }
    this.selectedSessionId = sessionId;
  }

  onAttachedSessionRowClick(session: any): void {
    this.selectedSessionId = session?.id || null;
    this.onSessionDetails(session);
  }

  onSessionDetails(session: any): void {
    const [instance, onClose] = this.detailPageService.loadComponent(SessionDetailsComponent);
    instance.session = session;
    instance.userSessions = false;
    onClose.then(() => {
      // Session detayı kapatıldı
    });
  }

  onDetachSession(session: any): void {
    if (!this.selectedTicket?.id || !session?.id) {
      return;
    }

    const roomId = this.selectedTicket?.room_id
      || this.selectedTicket?.room?.id
      || this.ticketDetail?.room?.id
      || session?.room_id;

    if (!roomId) {
      this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.TICKETS.FORM_INVALID'));
      return;
    }

    // Show confirmation modal
    const initialState = {
      headerTitle: this.translateService.instant('MAIN.CONTENT.TICKETS.DETACH_SESSION'),
      textMessage: this.translateService.instant('MAIN.CONTENT.TICKETS.DETACH_SESSION_CONFIRM'),
      confirmButtonText: this.translateService.instant('MAIN.CONTENT.TICKETS.DETACH')
    };
    let alertmodalRef = this.modal.show(AlertModalComponent, {
      initialState,
      backdrop: 'static',
      class: 'modal-dialog-centered',
      animated: false
    });
    alertmodalRef.content.onClose.subscribe(res => {
      if (res) {
        this.loaderService.show();
        this.ticketService.detachTicket(this.selectedTicket.id, roomId, session.id)
          .then(() => {
            this.snackbarService.success(this.translateService.instant('MAIN.CONTENT.TICKETS.DETACH_SUCCESS'));
          })
          .catch(() => {
            this.snackbarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR'));
          })
          .finally(() => this.loaderService.hide());
      } else {
        alertmodalRef.hide();
      }
    });
  }

  onAttachSession(): void {
    if (!this.selectedTicket?.id) {
      return;
    }

    const roomId = this.selectedTicket?.room_id
      || this.selectedTicket?.room?.id
      || this.ticketDetail?.room?.id
      || this.ticketForm.get('room_id')?.value;

    if (!roomId) {
      this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.TICKETS.FORM_INVALID'));
      return;
    }

    const dataModel = {
      sessions: [],
      loading: true,
      currentTicketId: this.selectedTicket.id
    };

    this.selectedSessionId = null;
    this.loaderService.show();
    this.sessionService.getUserSessions(roomId)
      .then(res => {
        const sessions = res?.sessions || [];
        dataModel.sessions = sessions
          .map(session => ({ ...session, room_id: (session as any).room_id || session.room?.id }))
          .sort((a, b) => (b.create_time || 0) - (a.create_time || 0));
        dataModel.loading = false;
      })
      .catch(() => {
        dataModel.sessions = [];
        dataModel.loading = false;
        this.snackbarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR'));
      })
      .finally(() => this.loaderService.hide());

    const modalId = this.templateModalService.show({
      template: this.pickSessionTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          close: () => {
            this.selectedSessionId = null;
            if (dataModel.loading) {
              this.loaderService.hide();
            }
            this.templateModalService.hide(modalId);
          },
          attach: () => {
            if (!this.selectedSessionId) {
              return;
            }
            const selectedSession = dataModel.sessions.find(s => s.id === this.selectedSessionId);
            if (selectedSession?.attached_ticket === dataModel.currentTicketId) {
              return;
            }
            this.loaderService.show();
            this.ticketService.attachTicket(this.selectedTicket.id, roomId, this.selectedSessionId)
              .then(() => {
                this.templateModalService.hide(modalId);
              })
              .catch(error => {
                if (error?.status === 409 || error?.error === 'session-already-attached') {
                  this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.TICKETS.SESSION_ALREADY_ATTACHED'));
                  return;
                }
                this.snackbarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR'));
              })
              .finally(() => this.loaderService.hide());
          }
        }
      }
    });
  }

  onSessionPicked(sessionId: string): void {
    this.selectedSessionId = sessionId;
  }

  isSelectedSessionAlreadyAttached(): boolean {
    if (!this.selectedSessionId) {
      return false;
    }
    return this.attachedSessionIds.has(this.selectedSessionId);
  }

  isSessionAttached(sessionId: string): boolean {
    return this.attachedSessionIds.has(sessionId);
  }

  openQuickSession(session: any): void {
    this.quickSession = session;
    this.loadQuickSessionDetails(session);
  }

  closeQuickSession(): void {
    this.clearQuickSessionSubs();
    this.quickSession = null;
    this.quickFiles = [];
    this.quickArchives = [];
    this.quickLogs = [];
    this.quickArchiveLinks = {};
  }

  getSessionRoomName(session: any): string {
    const roomId = (session as any)?.room_id || session?.room?.id;
    return session?.room?.name || (roomId ? this.getRoomName(roomId) : '-');
  }

  getQuickFileIcon(file: LogFile): string {
    const extension = this.getQuickFileExtension(file.name);
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
      return file.url;
    }
    if (extension === 'pdf') {
      return 'assets/img/pdf.png';
    }
    return 'assets/img/other.png';
  }

  getQuickFileExtension(filename?: string): string {
    if (!filename) {
      return '';
    }
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  isQuickPreviewable(file: LogFile): boolean {
    const ext = this.getQuickFileExtension(file.name);
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'pdf'].includes(ext);
  }

  onQuickFilePreview(file: LogFile): void {
    if (!file?.url) {
      return;
    }
    window.open(file.url, '_blank');
  }

  onQuickFileDownload(file: LogFile): void {
    if (!file?.url || !file?.name) {
      return;
    }
    this.fileShareService.downloadFile(file.url, file.name)
      .catch(() => this.snackbarService.error(this.translateService.instant('MAIN.SESSIONS.SESSION_FILES.DOWNLOAD_FAILED')));
  }

  onQuickArchiveDownload(archive: Archive): void {
    if (!this.quickSession || !archive?.id) {
      return;
    }
    const roomId = (this.quickSession as any)?.room_id || this.quickSession?.room?.id;
    if (!roomId) {
      return;
    }
    if (!(this.quickArchiveLinks[archive.id] && this.quickArchiveLinks[archive.id].url)) {
      this.quickArchiveLinks[archive.id] = { loading: true };
      this.sessionService.downloadArchive(roomId, this.quickSession.id, archive)
        .then(url => {
          this.quickArchiveLinks[archive.id].url = url;
          window.open(url, '_blank');
        })
        .catch(() => this.snackbarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR')))
        .finally(() => { this.quickArchiveLinks[archive.id].loading = false; });
    } else {
      window.open(this.quickArchiveLinks[archive.id].url, '_blank');
    }
  }

  getArchiveStatusKey(archive: Archive): string {
    switch (archive.storage_status) {
      case 'uploaded':
        return 'MAIN.ARCHIVES.ARCHIVE_LIST.AVAILABLE';
      case 'disconnected':
        return 'MAIN.ARCHIVES.ARCHIVE_LIST.DISCONNECTED';
      case 'expired':
      case 'deleted':
        return 'MAIN.ARCHIVES.ARCHIVE_LIST.DELETED';
      case 'pending':
        return 'MAIN.ARCHIVES.ARCHIVE_LIST.WAITING';
      case 'failed':
      default:
        return 'MAIN.ARCHIVES.ARCHIVE_LIST.FAILED';
    }
  }

  getArchiveBadgeClass(archive: Archive): string {
    switch (archive.storage_status) {
      case 'uploaded':
        return 'badge bg-success';
      case 'pending':
        return 'badge bg-warning text-dark';
      case 'disconnected':
        return 'badge bg-secondary';
      case 'expired':
      case 'deleted':
      case 'failed':
      default:
        return 'badge bg-danger';
    }
  }

  getQuickMessageTranslationKey(message: LogMessage): string {
    switch (message.type) {
      case 'info':
        if (message.type2 === 'joinRoom') {
          return 'MAIN.SESSIONS.SESSION_DETAILS.SESSION_CHAT.JOIN_ROOM';
        } else if (message.type2 === 'leaveRoom') {
          return 'MAIN.SESSIONS.SESSION_DETAILS.SESSION_CHAT.LEAVE_ROOM';
        }
        break;
      case 'request':
        if (message.type2 === 'screenshot') {
          return 'MAIN.SESSIONS.SESSION_DETAILS.SESSION_CHAT.REQUEST_SCREENSHOT';
        } else if (message.type2 === 'reaction') {
          return 'MAIN.SESSIONS.SESSION_DETAILS.SESSION_CHAT.REACTION';
        }
        break;
      case 'file':
        if (message.type2 === 'image') {
          return 'MAIN.SESSIONS.SESSION_DETAILS.SESSION_CHAT.SHARE_IMAGE';
        } else if (message.type2 === 'screenshot') {
          return 'MAIN.SESSIONS.SESSION_DETAILS.SESSION_CHAT.SHARE_SCREENSHOT';
        } else if (message.type2 === 'pdf') {
          return 'MAIN.SESSIONS.SESSION_DETAILS.SESSION_CHAT.SHARE_PDF';
        } else if (message.type2 === 'other') {
          return 'MAIN.SESSIONS.SESSION_DETAILS.SESSION_CHAT.SHARE_OTHER';
        }
        break;
      case 'object':
        return 'MAIN.SESSIONS.SESSION_DETAILS.SESSION_CHAT.SHARE_OBJECT';
      case 'collaboration':
        return 'MAIN.SESSIONS.SESSION_DETAILS.SESSION_CHAT.COLLABORATED';
    }
    return '';
  }

  private loadQuickSessionDetails(session: any): void {
    this.clearQuickSessionSubs();
    this.quickFiles = [];
    this.quickArchives = [];
    this.quickLogs = [];
    this.quickArchiveLinks = {};

    const roomId = (session as any)?.room_id || session?.room?.id;
    if (!roomId || !session?.id) {
      return;
    }

    this.quickFilesLoading = true;
    this.quickLogsLoading = true;
    this.quickArchivesLoading = true;

    this.quickFilesSub = this.sessionService.getSessionFiles(session)
      .subscribe(files => {
        this.quickFiles = files || [];
        this.quickFilesLoading = false;
      }, () => {
        this.quickFilesLoading = false;
      });

    this.quickLogsSub = this.sessionService.getSessionLogs(session)
      .subscribe(messages => {
        this.quickLogs = messages || [];
        this.quickLogsLoading = false;
      }, () => {
        this.quickLogsLoading = false;
      });

    this.quickArchivesSub = this.sessionService.getSessionArchives(roomId, session.id)
      .subscribe(archives => {
        this.quickArchives = (archives || []).sort((a, b) => (b.create_time || 0) - (a.create_time || 0));
        this.quickArchivesLoading = false;
      }, () => {
        this.quickArchivesLoading = false;
      });
  }

  private clearQuickSessionSubs(): void {
    this.quickFilesSub?.unsubscribe();
    this.quickLogsSub?.unsubscribe();
    this.quickArchivesSub?.unsubscribe();
    this.quickFilesSub = null;
    this.quickLogsSub = null;
    this.quickArchivesSub = null;
  }

  private detectTicketChanges(formValue: any, originalTicket: any) {
    const simpleTicketProps = Object.keys(originalTicket).filter(p => p !== 'followup');
    const changes: any = simpleTicketProps.reduce((changes, prop) => {
      if (prop === 'assignee') {
        const normalizedForm = formValue[prop] === '' ? null : formValue[prop];
        const normalizedOriginal = originalTicket[prop] === '' ? null : originalTicket[prop];
        if (normalizedForm !== normalizedOriginal) {
          changes[prop] = normalizedForm;
        }
        return changes;
      }

      if (formValue[prop] !== originalTicket[prop]) {
        changes[prop] = formValue[prop];
      }
      return changes;
    }, {});

    // Followup için özel kontrol
    if (formValue.followup) {
      if (!originalTicket.followup) {
        changes.followup = this.formatDate(formValue.followup);
      } else {
        const formFollowupDate = formValue.followup instanceof Date ? formValue.followup : new Date(formValue.followup);
        const originalFollowupDate = originalTicket.followup instanceof Date ? originalTicket.followup : new Date(originalTicket.followup);
        if (this.formatDate(formFollowupDate) !== this.formatDate(originalFollowupDate)) {
          changes.followup = this.formatDate(formFollowupDate);
        }
      }
    } else if (originalTicket.followup) {
      changes.followup = null;
    }

    return changes;
  }

  private buildTicketChangeEvents(changes: any, originalTicket: any): Array<{ key: string; params: any }> {
    if (!changes || Object.keys(changes).length === 0) {
      return [];
    }

    const actor = this.authService.currentUser?.name || '';
    const events: Array<{ key: string; params: any }> = [];

    const addValueChange = (field: string, key: string, options?: { fromLabel?: string; toLabel?: string }) => {
      if (!Object.prototype.hasOwnProperty.call(changes, field)) {
        return;
      }
      const params: any = {
        actor,
        field,
        from: originalTicket?.[field] ?? null,
        to: changes[field] ?? null
      };
      if (options?.fromLabel !== undefined) {
        params.fromLabel = options.fromLabel;
      }
      if (options?.toLabel !== undefined) {
        params.toLabel = options.toLabel;
      }
      events.push({ key, params });
    };

    addValueChange('status', 'MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SYSTEM.STATUS_CHANGED');
    addValueChange('priority', 'MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SYSTEM.PRIORITY_CHANGED');
    addValueChange('assignee', 'MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SYSTEM.ASSIGNEE_CHANGED');

    if (Object.prototype.hasOwnProperty.call(changes, 'followup')) {
      const fromDate = originalTicket?.followup ? new Date(originalTicket.followup).getTime() : null;
      const toDate = changes.followup ? new Date(changes.followup).getTime() : null;
      events.push({
        key: 'MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SYSTEM.DATE_CHANGED',
        params: {
          actor,
          field: 'followup',
          from: Number.isNaN(fromDate) ? null : fromDate,
          to: Number.isNaN(toDate) ? null : toDate
        }
      });
    }

    if (Object.prototype.hasOwnProperty.call(changes, 'room_id')) {
      const fromRoom = originalTicket?.room_id ? this.getRoomName(String(originalTicket.room_id)) : '-';
      const toRoom = changes.room_id ? this.getRoomName(String(changes.room_id)) : '-';
      addValueChange('room_id', 'MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SYSTEM.ROOM_CHANGED', {
        fromLabel: fromRoom,
        toLabel: toRoom
      });
    }

    if (Object.prototype.hasOwnProperty.call(changes, 'type')) {
      const fromType = originalTicket?.type ? this.getTicketTypeName(String(originalTicket.type)) : '-';
      const toType = changes.type ? this.getTicketTypeName(String(changes.type)) : '-';
      addValueChange('type', 'MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SYSTEM.TYPE_CHANGED', {
        fromLabel: fromType,
        toLabel: toType
      });
    }

    if (Object.prototype.hasOwnProperty.call(changes, 'title')) {
      events.push({
        key: 'MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SYSTEM.TITLE_UPDATED',
        params: { actor }
      });
    }

    if (Object.prototype.hasOwnProperty.call(changes, 'description')) {
      events.push({
        key: 'MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SYSTEM.DESCRIPTION_UPDATED',
        params: { actor }
      });
    }

    if (Object.prototype.hasOwnProperty.call(changes, 'resolution')) {
      events.push({
        key: 'MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SYSTEM.RESOLUTION_UPDATED',
        params: { actor }
      });
    }

    if (Object.prototype.hasOwnProperty.call(changes, 'extra_fields')) {
      events.push({
        key: 'MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SYSTEM.EXTRA_FIELDS_UPDATED',
        params: { actor }
      });
    }

    return events;
  }

  private buildSystemChatText(key: string, params: any): string {
    const payload = {
      key,
      params: params || {}
    };
    return `__system__:${JSON.stringify(payload)}`;
  }

  // File-related methods
  onDownloadFile(file: any): void {
    this.loaderService.show();
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    this.loaderService.hide();
    this.snackbarService.success(this.translateService.instant('MAIN.CONTENT.TICKETS.FILES.DOWNLOAD_STARTED'));
  }

  onFilePreview(file: any): void {
    if (this.isImageFile(file)) {
      // Open image in modal
      const initialState = {
        url: file.url,
        type: 'image',
        close: this.closeModal
      };
      this.modal.show(ImageModalComponent, {
        initialState,
        backdrop: 'static',
        class: 'modal-lg modal-dialog-centered',
        animated: false
      });
    } else {
      // Open PDF/other files in new tab
      window.open(file.url, '_blank');
    }
  }

  onChooseTicketFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFiles = input?.files;
    this.previewUpload(selectedFiles);
  }

  dragDropUpload(files: FileList): void {
    if (!environment.electron) {
      this.previewUpload(files);
    }
  }

  // Pending files management for create mode
  addPendingFiles(files: FileList): void {
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (file) {
        this.pendingFiles.push(file);
      }
    }
  }

  removePendingFile(index: number): void {
    const file = this.pendingFiles[index];
    // Revoke blob URL to prevent memory leak
    if (this.pendingFileUrls.has(file)) {
      URL.revokeObjectURL(this.pendingFileUrls.get(file));
      this.pendingFileUrls.delete(file);
    }
    this.pendingFiles.splice(index, 1);
  }

  onChooseCreateFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFiles = input?.files;
    if (selectedFiles) {
      this.addPendingFiles(selectedFiles);
    }
  }

  dragDropCreateFiles(files: FileList): void {
    if (!environment.electron) {
      this.addPendingFiles(files);
    }
  }

  isPendingImageFile(file: File): boolean {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    return this.imageExtensions.includes(extension);
  }

  isPendingPdfFile(file: File): boolean {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    return this.pdfExtensions.includes(extension);
  }

  getPendingFileUrl(file: File): string {
    if (!this.pendingFileUrls.has(file)) {
      this.pendingFileUrls.set(file, URL.createObjectURL(file));
    }
    return this.pendingFileUrls.get(file);
  }

  clearPendingFiles(): void {
    // Revoke all blob URLs to prevent memory leaks
    this.pendingFileUrls.forEach(url => URL.revokeObjectURL(url));
    this.pendingFileUrls.clear();
    this.pendingFiles = [];
  }

  private previewUpload(selectedFiles: FileList): void {
    if (!selectedFiles || !this.selectedTicket?.id) {
      return;
    }

    this.loaderService.show();
    this.uploadTicketFiles(selectedFiles)
      .then(() => {
        this.loaderService.hide();
      })
      .catch(() => {
        this.loaderService.hide();
        this.snackbarService.error(this.translateService.instant('ERRORS.UPLOAD-ERROR'));
      });
  }

  private uploadTicketFiles(files: FileList): Promise<any> {
    const formData = new FormData();
    for (let i = 0; i < files.length; i += 1) {
      const file = files.item(i);
      if (file) {
        formData.append(file.name, file);
      }
    }
    return this.ticketService.uploadTicketFiles(formData, this.selectedTicket.id, this.authService.currentUser.token)
      .then(() => {
        return new Promise(resolve => setTimeout(resolve, 500));
      });
  }

  onDeleteFile(file: any): void {
    if (!this.selectedTicket?.id || !file?.key) {
      return;
    }

    const initialState = {
      textMessage: this.translateService.instant('MAIN.CONTENT.WORKFLOW.WORKFLOW-DETAIL.DELETE-FILE'),
      headerTitle: this.translateService.instant('MAIN.CONTENT.WORKFLOW.HEADER-TITLES.DELETE-FILE')
    };

    const alertmodalRef = this.modalService.show(AlertModalComponent, {
      initialState,
      backdrop: 'static',
      class: 'modal-dialog-centered',
      animated: false
    });

    const sub = alertmodalRef.content.onClose.subscribe((res) => {
      if (res) {
        this.loaderService.show();
        this.ticketService.deleteTicketFile(this.selectedTicket.id, file.key)
          .catch(() => {
            this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.DASHBOARD.DELETE-ERROR'));
          })
          .finally(() => {
            this.loaderService.hide();
          });
      }
      sub.unsubscribe();
    });
  }

  getFileIcon(file: any): string {
    if (this.imageExtensions.includes(file.extension)) {
      return file.url;
    } else if (this.pdfExtensions.includes(file.extension)) {
      return 'assets/img/pdf.png';
    } else if (this.objectSpecificExtensions.includes(file.extension)) {
      return 'assets/img/obj.png';
    } else {
      return 'assets/img/other.png';
    }
  }

  isImageFile(file: any): boolean {
    return this.imageExtensions.includes(file.extension);
  }

  openTicketExportModal(): void {
    this.exportOptions = {
      includeSessions: true,
      includeFiles: true,
      includeChat: true,
      includeImagesInPdf: false
    };
    this.exportError = '';
    this.exportModalRef = this.modal.show(this.ticketExportTemplate, {
      backdrop: 'static',
      class: 'modal-dialog-centered ticket-export-dialog',
      animated: false
    });
  }

  closeTicketExportModal(): void {
    this.exportModalRef?.hide();
    this.exportModalRef = null;
  }

  isExportDisabled(): boolean {
    return !this.exportOptions.includeSessions
      && !this.exportOptions.includeFiles
      && !this.exportOptions.includeChat;
  }

  async exportTicket(): Promise<void> {
    if (!this.selectedTicket?.id || this.isExporting) {
      return;
    }

    if (this.isExportDisabled()) {
      this.exportError = this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.MIN_SELECTION');
      return;
    }

    if (!this.exportOptions.includeFiles) {
      this.exportOptions.includeImagesInPdf = false;
    }

    this.exportError = '';
    this.isExporting = true;
    this.loaderService.show();

    try {
      const ticketData = await firstValueFrom(this.ticketService.getTicketData(this.selectedTicket.id));
      const sessions = this.exportOptions.includeSessions ? this.mapTicketSessionsForExport(this.ticketSessions) : [];
      const files = this.exportOptions.includeFiles ? this.mapTicketFilesForExport(this.files) : [];
      const chatItems = this.exportOptions.includeChat
        ? this.buildTicketTimelineForExport(ticketData?.chat || {}, this.exportOptions.includeSessions ? this.ticketSessions : [], this.exportOptions.includeFiles ? this.files : [])
        : [];

      const pdfBlob = await this.pdfExportService.generateTicketPdfBlob({
        ticket: this.buildTicketExportInfo(),
        sessions,
        files,
        chat: chatItems,
        includeImagesInPdf: this.exportOptions.includeFiles && this.exportOptions.includeImagesInPdf,
        labels: {
          title: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.TITLE'),
          detailsTitle: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.DETAILS_TITLE'),
          sessionsTitle: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.SESSIONS_TITLE'),
          filesTitle: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.FILES_TITLE'),
          chatTitle: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.CHAT_TITLE'),
          labelTitle: this.translateService.instant('MAIN.CONTENT.TICKETS.TICKET_TITLE'),
          labelStatus: this.translateService.instant('MAIN.CONTENT.TICKETS.STATUS'),
          labelPriority: this.translateService.instant('MAIN.CONTENT.TICKETS.PRIORITY'),
          labelType: this.translateService.instant('MAIN.CONTENT.TICKETS.TYPE'),
          labelRoom: this.translateService.instant('MAIN.CONTENT.TICKETS.WORK_GROUP'),
          labelCreatedBy: this.translateService.instant('MAIN.CONTENT.TICKETS.CREATED_BY'),
          labelCreatedOn: this.translateService.instant('MAIN.CONTENT.TICKETS.CREATED_ON'),
          labelAssignee: this.translateService.instant('MAIN.CONTENT.TICKETS.ASSIGNEE'),
          labelFollowup: this.translateService.instant('MAIN.CONTENT.TICKETS.FOLLOWUP_DATE'),
          descriptionLabel: this.translateService.instant('MAIN.CONTENT.TICKETS.DESCRIPTION'),
          resolutionLabel: this.translateService.instant('MAIN.CONTENT.TICKETS.RESOLUTION'),
          noItems: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.NO_ITEMS'),
          tableDate: this.translateService.instant('MAIN.CONTENT.TICKETS.DATE'),
          tableRoom: this.translateService.instant('MAIN.CONTENT.TICKETS.ROOM_NAME'),
          tableParticipants: this.translateService.instant('MAIN.CONTENT.TICKETS.PARTICIPANTS'),
          tableDuration: this.translateService.instant('MAIN.CONTENT.TICKETS.DURATION'),
          tableFileName: this.translateService.instant('MAIN.CONTENT.TICKETS.FILES.TITLE'),
          tableFileType: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.FILE_TYPE'),
          tableFileSize: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.FILE_SIZE'),
          tableFileUploader: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.FILE_UPLOADER'),
          tableChatTime: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.CHAT_TIME'),
          tableChatUser: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.CHAT_USER'),
          tableChatMessage: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.CHAT_MESSAGE')
        }
      });

      const pdfFileName = this.buildTicketExportFileName('pdf');

      if (this.exportOptions.includeFiles) {
        const zipBlob = await this.buildTicketExportZip(pdfBlob, pdfFileName, this.files);
        saveAs(zipBlob, this.buildTicketExportFileName('zip'));
      } else {
        saveAs(pdfBlob, pdfFileName);
      }

      this.closeTicketExportModal();
    } catch (error) {
      this.snackbarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR'));
    } finally {
      this.loaderService.hide();
      this.isExporting = false;
    }
  }

  private buildTicketExportInfo() {
    const detail = (this.ticketDetail || {}) as any;
    const selected = (this.selectedTicket || {}) as any;
    const ticket: any = { ...selected, ...detail };
    const statusKey = ticket?.status ? `MAIN.CONTENT.TICKETS.STATUS_${String(ticket.status).toUpperCase()}` : '';
    const priorityKey = ticket?.priority ? `MAIN.CONTENT.TICKETS.PRIORITY_${String(ticket.priority).toUpperCase()}` : '';
    const createdOn = ticket?.create_time ? this.formatDateTime(ticket.create_time) : '';
    const followup = ticket?.followup ? this.formatDateTime(ticket.followup) : '';

    return {
      title: ticket?.title || '',
      status: statusKey ? this.translateService.instant(statusKey) : '',
      priority: priorityKey ? this.translateService.instant(priorityKey) : '',
      type: ticket?.type ? this.getTicketTypeName(ticket.type) : '',
      roomName: ticket?.room?.name || (ticket?.room_id ? this.getRoomName(ticket.room_id) : ''),
      createdBy: ticket?.creator?.name || ticket?.created_by || '',
      createdOn,
      assignee: ticket?.assignee?.name || (ticket?.assignee ? this.getUsersName(ticket.assignee) : ''),
      followup,
      description: ticket?.description || '',
      resolution: ticket?.resolution || ''
    };
  }

  private mapTicketSessionsForExport(sessions: any[]): Array<{ date: string; roomName: string; participants: string; duration: string }> {
    if (!Array.isArray(sessions)) {
      return [];
    }

    return sessions.map(session => {
      const created = session?.create_time ? this.formatDateTime(session.create_time) : '';
      const roomName = session?.room?.name || this.getRoomName(session?.room_id || session?.room?.id);
      const participantsCount = session?.users ? Object.keys(session.users).length : 0;
      const duration = this.getSessionDurationMinutes(session);
      return {
        date: created,
        roomName: roomName || '',
        participants: participantsCount ? String(participantsCount) : '',
        duration: duration ? `${duration} min` : ''
      };
    });
  }

  private mapTicketFilesForExport(files: any[]): Array<{ name: string; url: string; extension: string; size: string; uploader_name: string; uploader: string; isImage: boolean }> {
    if (!Array.isArray(files)) {
      return [];
    }

    return files.map(file => {
      const extension = file?.extension || (file?.name?.split('.')?.pop() || '').toLowerCase();
      const uploaderName = this.getFileOwnerName(file);
      return {
        name: file?.name || '',
        url: file?.url || '',
        extension: extension || '',
        size: file?.size ? this.formatFileSize(file.size) : '',
        uploader_name: uploaderName,
        uploader: uploaderName,
        isImage: extension ? this.imageExtensions.includes(extension) : false
      };
    });
  }

  private mapTicketChatMessages(chatObj: any): Array<{ timestampValue: number; timestamp: string; senderName: string; text: string }> {
    const messages: any[] = [];
    if (Array.isArray(chatObj)) {
      messages.push(...chatObj);
    } else if (chatObj && typeof chatObj === 'object') {
      Object.keys(chatObj).forEach(key => {
        messages.push({ id: key, ...chatObj[key] });
      });
    }

    return messages
      .map(msg => {
        const timestampValue = msg?.timestamp || msg?.createdAt || 0;
        const formattedText = this.formatSystemChatText(String(msg?.text || ''), msg?.userId, msg?.senderName);
        return {
          timestampValue,
          timestamp: timestampValue ? this.formatDateTime(timestampValue) : '',
          senderName: msg?.senderName || this.getUsersName(msg?.userId) || '',
          text: this.stripHtml(formattedText)
        };
      })
      .sort((a, b) => (a.timestampValue || 0) - (b.timestampValue || 0));
  }

  private formatSystemChatText(text: string, userId?: string, senderName?: string): string {
    const parsed = this.parseSystemChatText(text);
    if (!parsed) {
      return text;
    }
    const params = this.formatSystemParams(parsed.params || {}, { userId, senderName });
    return this.translateService.instant(parsed.key, params);
  }

  private parseSystemChatText(text: string): { key: string; params: any } | null {
    if (!text || typeof text !== 'string' || !text.startsWith('__system__:')) {
      return null;
    }
    try {
      const payload = JSON.parse(text.replace('__system__:', ''));
      if (!payload?.key) {
        return null;
      }
      return { key: payload.key, params: payload.params || {} };
    } catch (error) {
      return null;
    }
  }

  private formatSystemParams(params: any, context: { userId?: string; senderName?: string }): any {
    const field = params?.field;
    const formatted: any = { ...params };

    const fallbackActor = this.translateService.instant('MAIN.CONTENT.TICKETS.CHAT_TIMELINE.SYSTEM.DEFAULT_ACTOR');
    formatted.actor = params?.actor || context?.senderName || this.getUsersName(context?.userId) || fallbackActor;

    if (!field) {
      return formatted;
    }

    formatted.from = this.formatSystemValue(field, params?.from, params?.fromLabel);
    formatted.to = this.formatSystemValue(field, params?.to, params?.toLabel);
    return formatted;
  }

  private formatSystemValue(field: string, value: any, label?: string): string {
    if (label) {
      return label;
    }
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (field === 'status') {
      const key = `MAIN.CONTENT.TICKETS.STATUS_${String(value).toUpperCase().replace('-', '_')}`;
      return this.translateService.instant(key);
    }

    if (field === 'priority') {
      const key = `MAIN.CONTENT.TICKETS.PRIORITY_${String(value).toUpperCase()}`;
      return this.translateService.instant(key);
    }

    if (field === 'assignee') {
      return this.getUsersName(String(value));
    }

    if (field === 'room_id') {
      return this.getRoomName(String(value));
    }

    if (field === 'type') {
      return this.getTicketTypeName(String(value));
    }

    if (field === 'followup') {
      const dateValue = new Date(value);
      if (Number.isNaN(dateValue.getTime())) {
        return String(value);
      }
      return this.formatDateTime(dateValue);
    }

    return String(value);
  }

  private buildTicketTimelineForExport(chatObj: any, sessions: any[], files: any[]): Array<{ timestamp: string; senderName: string; text: string }> {
    const timeline: Array<{ timestampValue: number; timestamp: string; senderName: string; text: string }> = [];

    const chatMessages = this.mapTicketChatMessages(chatObj);
    chatMessages.forEach(item => {
      timeline.push({
        timestampValue: item.timestampValue || 0,
        timestamp: item.timestamp,
        senderName: item.senderName,
        text: item.text
      });
    });

    (sessions || []).forEach(session => {
      const timestampValue = session?.create_time || session?.start_time || 0;
      timeline.push({
        timestampValue,
        timestamp: timestampValue ? this.formatDateTime(timestampValue) : '',
        senderName: '',
        text: this.translateService.instant('MAIN.CONTENT.TICKETS.EXPORT_PDF.SESSION_HELD')
      });
    });

    (files || []).forEach(file => {
      const timestampValue = file?.create_time || file?.upload_time || file?.timestamp || 0;
      const ownerName = this.getFileOwnerName(file);
      const fileName = file?.name || '';
      const textKey = ownerName
        ? 'MAIN.CONTENT.TICKETS.EXPORT_PDF.FILE_SHARED_BY'
        : 'MAIN.CONTENT.TICKETS.EXPORT_PDF.FILE_SHARED';
      const text = this.translateService.instant(textKey, { file: fileName, name: ownerName });
      timeline.push({
        timestampValue,
        timestamp: timestampValue ? this.formatDateTime(timestampValue) : '',
        senderName: ownerName,
        text
      });
    });

    return timeline
      .sort((a, b) => (a.timestampValue || 0) - (b.timestampValue || 0))
      .map(({ timestampValue, ...rest }) => rest);
  }

  private getSessionDurationMinutes(session: any): number {
    const start = session?.start_time;
    const end = session?.end_time;
    if (!start || !end) {
      return 0;
    }
    return Math.ceil((end - start) / 60000);
  }

  private formatDateTime(value: any): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleString();
  }

  private formatFileSize(bytes: number): string {
    if (!bytes || bytes <= 0) {
      return '';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  }

  private stripHtml(value: string): string {
    if (!value) {
      return '';
    }
    const div = document.createElement('div');
    div.innerHTML = value;
    return div.textContent || div.innerText || '';
  }

  private getFileOwnerName(file: any): string {
    if (!file) {
      return '';
    }
    return (
      file?.uploader_name ||
      file?.owner_info ||
      file?.owner?.name ||
      file?.uploader?.name ||
      file?.user?.name ||
      file?.creator?.name ||
      file?.created_by ||
      (file?.uploader_id ? this.getUsersName(file.uploader_id) : '') ||
      ''
    );
  }

  private async buildTicketExportZip(pdfBlob: Blob, pdfFileName: string, files: any[]): Promise<Blob> {
    const zip = new JSZip();
    zip.file(pdfFileName, pdfBlob);
    const usedNames = new Map<string, number>();

    if (Array.isArray(files)) {
      for (const file of files) {
        const url = file?.url;
        if (!url) {
          continue;
        }
        try {
          const response = await fetch(url);
          if (!response.ok) {
            continue;
          }
          const blob = await response.blob();
          const filename = this.sanitizeFileNameWithExtension(file?.name || 'file');
          const uniqueName = this.ensureUniqueZipFileName(filename, usedNames);
          zip.file(`files/${uniqueName}`, blob);
        } catch (error) {
          continue;
        }
      }
    }

    return zip.generateAsync({ type: 'blob' });
  }

  private buildTicketExportFileName(extension: 'pdf' | 'zip'): string {
    const title = this.selectedTicket?.title || 'ticket';
    const sanitized = this.sanitizeFileName(title);
    const timestamp = new Date().toISOString().split('T')[0];
    return `${sanitized}_export_${timestamp}.${extension}`;
  }

  private sanitizeFileName(value: string): string {
    const normalized = (value || 'ticket')
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '')
      .trim();
    return normalized || 'ticket';
  }

  getEstimatedExportSizeLabel(): string {
    const baseMb = 0.2;
    const sessionsMb = this.exportOptions.includeSessions ? 0.2 : 0;
    const chatMb = this.exportOptions.includeChat ? 0.1 : 0;
    const embedImagesMb = this.exportOptions.includeImagesInPdf
      ? this.getImageFileCount() * 0.3
      : 0;
    const filesMb = this.exportOptions.includeFiles ? this.getFilesTotalSizeMb() : 0;

    const estimated = baseMb + sessionsMb + chatMb + embedImagesMb + filesMb;
    const rounded = Math.max(0.2, Math.round(estimated * 10) / 10);
    return `~${rounded.toFixed(1)} MB`;
  }

  private getFilesTotalSizeMb(): number {
    if (!Array.isArray(this.files) || this.files.length === 0) {
      return 0.5;
    }

    const totalBytes = this.files
      .map(file => Number(file?.size || 0))
      .filter(size => Number.isFinite(size) && size > 0)
      .reduce((sum, size) => sum + size, 0);

    if (!totalBytes) {
      return Math.max(0.5, this.files.length * 0.4);
    }

    return totalBytes / (1024 * 1024);
  }

  private getImageFileCount(): number {
    if (!Array.isArray(this.files) || this.files.length === 0) {
      return 0;
    }

    return this.files.filter(file => this.isImageFile(file)).length;
  }

  private sanitizeFileNameWithExtension(value: string): string {
    const rawName = value || 'file';
    const lastDotIndex = rawName.lastIndexOf('.');
    if (lastDotIndex <= 0 || lastDotIndex === rawName.length - 1) {
      return this.sanitizeFileName(rawName) || 'file';
    }

    const baseName = rawName.slice(0, lastDotIndex);
    const extension = rawName.slice(lastDotIndex + 1);
    const sanitizedBase = this.sanitizeFileName(baseName) || 'file';
    const sanitizedExtension = extension.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

    if (!sanitizedExtension) {
      return sanitizedBase;
    }

    return `${sanitizedBase}.${sanitizedExtension}`;
  }

  private ensureUniqueZipFileName(fileName: string, usedNames: Map<string, number>): string {
    const existing = usedNames.get(fileName);
    if (existing === undefined) {
      usedNames.set(fileName, 1);
      return fileName;
    }

    const nextIndex = existing + 1;
    usedNames.set(fileName, nextIndex);

    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
      return `${fileName}_${nextIndex}`;
    }

    const base = fileName.slice(0, lastDotIndex);
    const extension = fileName.slice(lastDotIndex + 1);
    return `${base}_${nextIndex}.${extension}`;
  }

  formatTimestamp(timestamp: number): string {
    if (!timestamp && timestamp !== 0) {
      return '-';
    }
    const d = new Date(timestamp);
    return d.toLocaleString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getDuration(startTime: number, endTime: number): number {
    return Math.ceil((endTime - startTime) / 60000);
  }
}

